import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Package, BarChart3, FileText, KeyRound, LogOut, Menu, X,
  Users, Receipt, FileSignature, Settings as SettingsIcon,
  Wallet, UserCog, CalendarCheck, ShieldCheck, History,
  ReceiptText, BookText, FileBarChart,
  CalendarDays, Shield, Car, ClipboardCheck, CalendarClock,
  Target, Award, Grid3x3, BookOpen, CreditCard, Building2, Inbox, Lock, Layers,
  HandCoins, LifeBuoy, ClipboardList, Star, Truck, Wrench, Activity, ChevronDown,
  FileMinus2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useConsoleTheme } from '../../contexts/ThemeContext';
import { SubscriptionBanner } from './SubscriptionBanner';
import { HelpPanel } from './HelpPanel';
import { ThemeChoice } from './ThemeChoice';
import { LanguagePicker } from './LanguagePicker';
import { API } from '../../lib/api';

// Each nav item declares which permission key is required (or 'master' for
// master-admin-only items). Items without a key are visible to every admin.
export const NAV = [
  { to: '/admin/billing/customers',    label: 'Customers',    icon: Users,          testid: 'nav-customers',         match: (p) => p.startsWith('/admin/billing/customers'), perm: 'can_manage_customers' },
  { to: '/admin/appointments',         label: 'Appointments', icon: CalendarClock,  testid: 'nav-appointments',      match: (p) => p.startsWith('/admin/appointments'), perm: ['can_manage_appointments', 'can_manage_inspections'] },
  { to: '/admin/inspections',          label: 'Inspections',  icon: FileText,       testid: 'nav-inspections',       match: (p) => p.startsWith('/admin/inspections'), perm: 'can_manage_inspections' },
  { to: '/admin/job-cards',            label: 'Job Cards',    icon: ClipboardList,  testid: 'nav-job-cards',         match: (p) => p.startsWith('/admin/job-cards'), perm: ['can_manage_invoices', 'can_manage_estimates', 'can_manage_inspections'] },
  { to: '/admin/billing/estimates',    label: 'Estimates (Non GST)', icon: FileSignature,  testid: 'nav-estimates-top',         match: (p) => p.startsWith('/admin/billing/estimates'), perm: 'can_manage_estimates' },
  { to: '/admin/billing/invoices',     label: 'Invoices',     icon: Receipt,        testid: 'nav-invoices',          match: (p) => p.startsWith('/admin/billing/invoices'), perm: 'can_manage_invoices' },
  { to: '/admin/billing/warranties',   label: 'eWarranties',  icon: Shield,         testid: 'nav-warranties',        perm: ['can_manage_invoices', 'can_view_invoices'] },
  { to: '/admin/billing/collections',  label: 'Collections',  icon: HandCoins,      testid: 'nav-collections',       perm: ['can_manage_invoices', 'can_view_invoices'] },
  { to: '/admin/feedback',             label: 'Feedback',     icon: Star,           testid: 'nav-feedback',          perm: ['can_manage_customers', 'can_manage_invoices'] },
  { to: '/admin/inventory',            label: 'Inventory',    icon: Package,        testid: 'nav-inventory',         perm: 'can_manage_inventory' },
  { to: '/admin/vendors',              label: 'Vendor Management', icon: Truck,          testid: 'nav-vendors',           perm: 'can_manage_inventory' },
  { to: '/admin/labour-chart',         label: 'Labour Chart', icon: Wrench,         testid: 'nav-labour-chart',      perm: ['can_manage_billing_settings', 'can_manage_estimates', 'can_manage_invoices'] },
  { to: '/admin/expenses',             label: 'Expenses',     icon: Wallet,         testid: 'nav-expenses',          match: (p) => p.startsWith('/admin/expenses'), perm: ['can_view_expenses', 'can_manage_expenses'] },
  { group: 'Team Management' },
  { to: '/admin/staff',                label: 'Staff Master', icon: UserCog,        testid: 'nav-staff',             match: (p) => p.startsWith('/admin/staff'), perm: 'can_manage_staff' },
  { to: '/admin/my-attendance',        label: 'My Attendance', icon: CalendarDays,  testid: 'nav-my-attendance',     perm: 'can_self_mark_attendance' },
  { to: '/admin/payroll',              label: 'Payroll',      icon: CalendarCheck,  testid: 'nav-payroll',           match: (p) => p.startsWith('/admin/payroll'), perm: 'can_view_payroll' },
  { group: 'Performance Management System' },
  { to: '/admin/performance',          label: 'Team Reviews', icon: Target,         testid: 'nav-performance',       match: (p) => p.startsWith('/admin/performance'), perm: ['can_manage_performance', 'can_review_team_performance'] },
  { to: '/admin/skills',               label: 'Skill Matrix', icon: Grid3x3,        testid: 'nav-skills',            match: (p) => p.startsWith('/admin/skills'), perm: ['can_manage_performance', 'can_review_team_performance'] },
  { to: '/admin/my-performance',       label: 'My Performance', icon: Award,        testid: 'nav-my-performance' },
  { to: '/admin/sop',                  label: 'SOP & Process Flow', icon: BookOpen, testid: 'nav-sop',               match: (p) => p.startsWith('/admin/sop') },
  { group: null },
  { to: '/admin/settings',             label: 'Settings',     icon: SettingsIcon,  testid: 'nav-settings',  match: (p) => p === '/admin/settings' || p === '/admin/billing/settings', perm: 'can_manage_billing_settings' },
  { to: '/admin/reports',              label: 'Reports',      icon: BarChart3,      testid: 'nav-reports',           match: (p) => p.startsWith('/admin/reports'), perm: 'can_view_reports' },
  { group: 'Pre-owned Marketplace' },
  { to: '/admin/marketplace',          label: 'Cars & Leads', icon: Car,            testid: 'nav-marketplace',       match: (p) => p.startsWith('/admin/marketplace'), perm: 'can_manage_marketplace' },
  { to: '/admin/used-car-inspections', label: 'Used Car Inspections', icon: ClipboardCheck, testid: 'nav-used-car-inspections', match: (p) => p.startsWith('/admin/used-car-inspections'), perm: ['can_manage_used_car_inspections', 'can_manage_inspections'] },
  { group: 'GST Invoicing' },
  { to: '/admin/billing/estimates',    label: 'Estimates',    icon: FileSignature,  testid: 'nav-estimates',         match: (p) => p.startsWith('/admin/billing/estimates'), perm: 'can_manage_estimates' },
  { to: '/admin/gst/invoices',         label: 'GST Invoices', icon: ReceiptText,    testid: 'nav-gst-invoices',      match: (p) => p.startsWith('/admin/gst/invoices'), perm: ['can_manage_invoices', 'can_view_invoices'] },
  { to: '/admin/gst/notes',            label: 'Credit & Debit Notes', icon: FileMinus2, testid: 'nav-gst-notes', match: (p) => p.startsWith('/admin/gst/notes'), perm: ['can_manage_invoices', 'can_view_invoices'] },
  { to: '/admin/gst/hsn',              label: 'HSN/SAC Master', icon: BookText,     testid: 'nav-gst-hsn',           match: (p) => p.startsWith('/admin/gst/hsn'), perm: ['can_manage_invoices', 'can_view_invoices'] },
  { to: '/admin/gst/returns',          label: 'GST Returns',  icon: FileBarChart,   testid: 'nav-gst-returns',       match: (p) => p.startsWith('/admin/gst/returns'), perm: 'can_view_reports' },
  { to: '/admin/gst/settings',         label: 'GST Setup',    icon: SettingsIcon,   testid: 'nav-gst-settings',      perm: 'can_manage_billing_settings' },
  { group: null },
  { to: '/admin/users',                label: 'Admin Users',  icon: ShieldCheck,    testid: 'nav-admin-users',       perm: 'master' },
  { to: '/admin/activity-log',         label: 'Activity Log', icon: History,        testid: 'nav-activity-log',      perm: 'master' },
  { to: '/admin/subscription',         label: 'Plan & Billing', icon: CreditCard,   testid: 'nav-subscription',      perm: 'master' },
  { to: '/admin/platform',             label: 'Workshop Accounts', icon: Building2, testid: 'nav-platform',          platformOnly: true },
  { to: '/admin/platform/packages',    label: 'Packages & Modules', icon: Layers,   testid: 'nav-platform-packages', platformOnly: true },
  { to: '/admin/platform/leads',       label: 'Driveline Enquiries', icon: Inbox,   testid: 'nav-platform-leads',    platformOnly: true },
  { to: '/admin/platform/migrations',  label: 'Migrations',          icon: Inbox,   testid: 'nav-platform-migrations', platformOnly: true },
  { to: '/admin/platform/support',     label: 'Support Tickets',     icon: LifeBuoy, testid: 'nav-platform-support',  platformOnly: true },
  { to: '/admin/platform/health',      label: 'App Health',          icon: Activity, testid: 'nav-platform-health',   platformOnly: true },
  { to: '/admin/settings/password',    label: 'Password',     icon: KeyRound,       testid: 'nav-password' },
];

// Nav path -> the plan feature it needs. Mirrors `_FEATURE_PATHS` in the
// backend's tenant_runtime, which is what actually enforces this; hiding the
// link just stops people walking into a 403.
const NAV_FEATURE = [
  ['/admin/inspections', 'inspections'],
  ['/admin/used-car-inspections', 'inspections'],
  ['/admin/inventory', 'inventory'],
  ['/admin/billing/warranties', 'ewarranty'],
  ['/admin/payroll', 'payroll'],
  ['/admin/my-attendance', 'payroll'],
  ['/admin/appointments', 'appointments'],
  ['/admin/gst', 'gst'],
  ['/admin/expenses', 'expenses'],
  ['/admin/performance', 'performance'],
  ['/admin/my-performance', 'performance'],
  ['/admin/skills', 'skills'],
  ['/admin/sop', 'sop'],
  ['/admin/marketplace', 'marketplace'],
  ['/admin/billing/estimates', 'estimates'],
  ['/admin/reports', 'reports_basic'],
];

export const navFeature = (item) => {
  if (!item?.to) return null;
  const hit = NAV_FEATURE.find(([prefix]) => item.to.startsWith(prefix));
  return hit ? hit[1] : null;
};

export const canSeeNavItem = (item, user, hasFeature) => {
  const feature = navFeature(item);
  if (feature && hasFeature && !hasFeature(feature)) return false;
  if (item.platformOnly) return Boolean(user?.is_platform_owner);
  if (!item.perm) return true;
  if (!user) return false;
  if (user.is_master_admin) return true;
  if (item.perm === 'master') return false; // master-only and we're not master
  const perms = user.permissions || {};
  // Nav items can declare a single permission key OR an array (any-of).
  if (Array.isArray(item.perm)) return item.perm.some((k) => Boolean(perms[k]));
  return Boolean(perms[item.perm]);
};

// First screen this user is actually allowed into — used as the post-login
// landing so a permission-limited admin never opens on a module that is not
// even in their sidebar.
export const firstAllowedPath = (user) => {
  const item = NAV.find((i) => i.to && i.to !== '/admin/settings/password'
    && canSeeNavItem(i, user));
  return (item && item.to) || '/admin/settings/password';
};

// Can this user open an arbitrary /admin path? Used by ProtectedRoute and to
// validate a `from` route carried across a user switch on the login screen.
export const canOpenPath = (path, user) => {
  if (!path || !user) return false;
  // Settings used to live under /admin/billing/settings; old bookmarks still
  // resolve to the same screen and must not read as denied.
  const target = path.startsWith('/admin/billing/settings') ? '/admin/settings' : path;
  if (user.is_master_admin) return true;
  // Own self-service screens are always available, whatever the grants are.
  if (['/admin/my-attendance', '/admin/my-performance', '/admin/settings/password']
    .some((p) => target.startsWith(p))) return true;
  const item = NAV.filter((i) => i.to)
    .sort((a, b) => b.to.length - a.to.length)
    .find((i) => target.startsWith(i.to));
  return item ? canSeeNavItem(item, user) : false;
};

const GROUPS_KEY = 'dl_nav_open_groups';
const slug = (g) => g.toLowerCase().replace(/\s+/g, '-');

const GroupHeader = ({ name, open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    data-testid={`nav-group-toggle-${slug(name)}`}
    className="w-full pt-3 pb-1 px-3 text-[10px] uppercase tracking-widest font-semibold text-teal-300/70 hover:text-teal-200 flex items-center gap-1.5 transition-colors"
  >
    <span className="h-px flex-1 bg-white/10" />
    <span data-testid={`nav-group-${slug(name)}`}>{name}</span>
    <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
    <span className="h-px flex-1 bg-white/10" />
  </button>
);

// Folded by default; the choice is remembered on this device.
const useOpenGroups = () => {
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(GROUPS_KEY)) || []; } catch { return []; }
  });
  const save = (next) => { setOpen(next); localStorage.setItem(GROUPS_KEY, JSON.stringify(next)); };
  // Opening a screen unfolds the section it lives in.
  const activeGroup = (() => {
    let g = null;
    for (const n of NAV) {
      if (n.group !== undefined) { g = n.group; continue; }
      if (n.match ? n.match(location.pathname) : location.pathname === n.to) return g;
    }
    return null;
  })();
  useEffect(() => {
    if (activeGroup && !open.includes(activeGroup)) save([...open, activeGroup]);
  }, [activeGroup]);
  const isOpen = (g) => open.includes(g);
  const toggle = (g) => save(open.includes(g) ? open.filter((x) => x !== g) : [...open, g]);
  return { isOpen, toggle };
};

const SidebarNav = ({ user, hasFeature, isViewOnly, moduleInfo, onItemClick }) => {
  const { isOpen, toggle } = useOpenGroups();
        // Build a filtered list, then suppress group headers that have no
        // visible items beneath them (so staff users don't see empty
        // "GST INVOICING" / "BILLING" labels with nothing under them).
        // Modules outside the plan stay in the list on purpose — they render
        // locked, which is how a workshop finds out an upgrade exists.
        const filtered = NAV.filter((item) => item.group !== undefined || canSeeNavItem(item, user));
        const trimmed = [];
        for (let i = 0; i < filtered.length; i += 1) {
          const item = filtered[i];
          if (item.group !== undefined && item.group !== null) {
            // Look ahead: is there at least one nav row before the next group?
            let hasChild = false;
            for (let j = i + 1; j < filtered.length; j += 1) {
              const next = filtered[j];
              if (next.group !== undefined) break;
              hasChild = true;
              break;
            }
            if (!hasChild) continue;
          }
          trimmed.push(item);
        }
        let current = null;
        return trimmed.map((item, idx) => {
          if (item.group !== undefined) {
            current = item.group;
            if (item.group === null) return <div key={`spacer-${idx}`} className="h-2" />;
            return <GroupHeader key={`group-${idx}`} name={item.group} open={isOpen(item.group)} onToggle={() => toggle(item.group)} />;
          }
          if (current && !isOpen(current)) return null;
          const feature = navFeature(item);
          if (feature && hasFeature && !hasFeature(feature)) {
            // Paid for it once? The records stay reachable, read-only.
            if (isViewOnly && isViewOnly(feature)) {
              return <ViewOnlyNavRow key={item.testid} item={item} onClick={onItemClick} />;
            }
            return (
              <LockedNavRow
                key={item.testid}
                item={item}
                info={moduleInfo ? moduleInfo(feature) : null}
                onClick={onItemClick}
              />
            );
          }
          return <NavRow key={item.testid} item={item} onClick={onItemClick} />;
        });};

// Unhappy (1–2★) ratings nobody has followed up yet.
const FeedbackAlertBadge = () => {
  const { authHeader } = useAuth();
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => axios.get(`${API}/feedback/google-rating`, { headers: authHeader() })
      .then((r) => setN(r.data.alerts || 0)).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [authHeader]);
  if (!n) return null;
  return <span className="ml-auto rounded-full bg-rose-600 px-1.5 text-[10px] font-bold" style={{ color: '#fff' }} data-testid="nav-feedback-alert">{n}</span>;
};

const NavRow = ({ item, onClick }) => {
  const Icon = item.icon;
  const location = useLocation();
  // Custom match for /admin/inspections + /admin/inspections/* (so the
  // sidebar highlights "Inspections" even when editing a specific record).
  const isActive = item.match
    ? item.match(location.pathname)
    : location.pathname === item.to;
  return (
    <NavLink
      to={item.to}
      end={!item.match}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors group ${
        isActive
          ? 'bg-teal-500/20 text-teal-200'
          : 'text-slate-300 hover:bg-white/5 hover:text-white'
      }`}      data-testid={item.testid}
    >
      <Icon size={18} className={isActive ? 'text-teal-300' : 'text-slate-400 group-hover:text-slate-200'} />
      <span>{item.label}</span>
      {item.testid === 'nav-feedback' && <FeedbackAlertBadge />}
    </NavLink>
  );
};

/** A module this workshop is not paying for: visible, but locked. */
const LockedNavRow = ({ item, info, onClick }) => {
  const navigate = useNavigate();
  const Icon = item.icon;
  const on = info?.included_in?.length ? info.included_in[0] : null;
  return (
    <button
      type="button"
      onClick={() => { navigate('/admin/subscription'); if (onClick) onClick(); }}
      title={on ? `Available on ${info.included_in.join(' and ')}` : 'Not included in your plan'}
      className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-white/5 hover:text-slate-300 transition-colors group"
      data-testid={`${item.testid}-locked`}
    >
      <Icon size={18} className="text-slate-600 group-hover:text-slate-500" />
      <span className="flex-1 text-left truncate">{item.label}</span>
      <Lock size={13} className="text-slate-600 group-hover:text-teal-400 shrink-0" />
    </button>
  );
};

/** A module they used to pay for: records stay reachable, writing does not. */
const ViewOnlyNavRow = ({ item, onClick }) => {
  const Icon = item.icon;
  const location = useLocation();
  const isActive = item.match ? item.match(location.pathname) : location.pathname === item.to;
  return (
    <NavLink
      to={item.to}
      end={!item.match}
      onClick={onClick}
      title="No longer in your plan — you can view and download your records"
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors group ${
        isActive ? 'bg-white/10 text-slate-200' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
      }`}
      data-testid={`${item.testid}-view-only`}
    >
      <Icon size={18} className="text-slate-500 group-hover:text-slate-300" />
      <span className="flex-1 truncate">{item.label}</span>
      <span className="text-[9px] uppercase tracking-wider text-slate-500 shrink-0">View</span>
    </NavLink>
  );
};

const SidebarContent = ({ onItemClick, onLogout, user, hasFeature, moduleInfo, isViewOnly, workshopLogo, workshopName }) => (
  <>
    {/* Brand — the workshop's own mark when they've uploaded one, with
        Driveline kept underneath as the platform it runs on. */}
    <div className="px-2 py-1 mb-6" data-testid="sidebar-brand">
      {workshopLogo ? (
        <>
          {/* Light plate: the sidebar is near-black, and a workshop's logo is
              usually drawn for white paper. */}
          <span className="flex w-full justify-center bg-white/95 rounded-lg px-3 py-2.5">
            <img
              src={workshopLogo}
              alt={workshopName || 'Workshop'}
              className="max-h-20 w-full object-contain"
              data-testid="sidebar-workshop-logo"
            />
          </span>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-[0.18em]">
            <span>on</span>
            <img src="/brand/driveline-logo-white.png" alt="Driveline" className="h-6 w-auto opacity-80" />
          </div>
        </>
      ) : (
        <>
          <img
            src="/brand/driveline-logo-white.png"
            alt="Driveline"
            className="h-12 w-auto"
          />
          <div className="mt-2 text-[10px] text-slate-500 uppercase tracking-[0.18em]">
            Admin Console
          </div>
        </>
      )}
    </div>

    {/* Nav */}
    <nav className="space-y-1 mb-6 flex-1" aria-label="Admin navigation">
      <SidebarNav user={user} hasFeature={hasFeature} isViewOnly={isViewOnly} moduleInfo={moduleInfo} onItemClick={onItemClick} />
    </nav>

    {/* Footer: appearance, user + logout */}
    <div className="border-t border-white/10 pt-4 space-y-3">
      <div className="px-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Appearance</div>
        <ThemeChoice compact />
      </div>
      {user && (
        <div className="px-2">
          <div className="text-sm font-medium text-white truncate" data-testid="sidebar-user-email">{user.email}</div>
          <div className="text-xs text-slate-400 capitalize flex items-center gap-1">
            {user.is_master_admin && <ShieldCheck size={11} className="text-teal-300" />}
            {user.is_master_admin ? 'Master Admin' : user.role}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-red-500/10 hover:text-red-300 transition-colors"
        data-testid="sidebar-logout"
      >
        <LogOut size={18} />
        <span>Logout</span>
      </button>
    </div>
  </>
);

const AdminLayout = () => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [unseenNews, setUnseenNews] = useState(0);
  const dark = useConsoleTheme();
  const { user, logout, authHeader } = useAuth();
  const { hasFeature, moduleInfo, isViewOnlyFeature, access, logoUrl, tenant } = useTenant();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const onLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate('/admin/login', { replace: true, state: null });
  };

  const closeMobile = () => setMobileOpen(false);

  // A changelog nobody notices is a changelog nobody reads, so the Help button
  // carries the count until the tab is opened.
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/help/whats-new`, { headers: authHeader() })
      .then(({ data }) => setUnseenNews(data.unseen || 0))
      .catch(() => { /* the panel still works without the badge */ });
  }, [user, authHeader]);

  // Page title for the mobile top bar (derived from the active nav item).
  const activeNav = NAV.find((n) => n.group === undefined && (n.match ? n.match(location.pathname) : location.pathname === n.to));
  const pageTitle = activeNav?.label || 'Admin';

  // Landing on a module outside the plan (post-login default, or a stale
  // bookmark) shows an empty page full of 403s — send them to the plan screen
  // instead. View-only modules are exempt: their records are still theirs.
  useEffect(() => {
    const feature = activeNav ? navFeature(activeNav) : null;
    if (!feature || !access) return;
    if (!hasFeature(feature) && !isViewOnlyFeature(feature)) {
      navigate('/admin/subscription', { replace: true });
    }
  }, [activeNav, access, hasFeature, isViewOnlyFeature, navigate]);

  return (
    <div className={`admin-scope ${dark ? 'admin-dark' : 'admin-light'} min-h-screen bg-slate-950 text-slate-100 lg:flex`}>
      {/* ─── Desktop sidebar (md+) ─── */}
      <aside className="hidden lg:flex lg:flex-col fixed inset-y-0 left-0 w-64 bg-slate-950 border-r border-white/10 px-4 py-6 z-40 admin-sidebar-dark overflow-y-auto" data-testid="admin-sidebar-desktop">
        <SidebarContent onItemClick={undefined} onLogout={onLogout} user={user} hasFeature={hasFeature} moduleInfo={moduleInfo} isViewOnly={isViewOnlyFeature} workshopLogo={logoUrl} workshopName={tenant?.workshop_name} />
      </aside>

      {/* ─── Mobile top bar (lg-) ─── */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 rounded-md hover:bg-slate-100"
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <Menu size={22} />
        </button>
        <h1 className="text-base font-semibold tracking-tight" data-testid="mobile-page-title">{pageTitle}</h1>
        {location.pathname.startsWith('/admin/my-performance')
          ? <LanguagePicker />
          : <div className="w-9" />}
      </header>

      {/* ─── Mobile drawer (lg-) ─── */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
            onClick={closeMobile}
            data-testid="mobile-drawer-backdrop"
          />
          <aside
            className="lg:hidden fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-slate-950 border-r border-white/10 px-4 py-6 z-50 flex flex-col admin-sidebar-dark overflow-y-auto"
            data-testid="admin-sidebar-mobile"
          >
            <button
              type="button"
              onClick={closeMobile}
              className="absolute top-4 right-4 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10"
              aria-label="Close menu"
              data-testid="mobile-close-btn"
            >
              <X size={18} />
            </button>
            <SidebarContent onItemClick={closeMobile} onLogout={onLogout} user={user} hasFeature={hasFeature} moduleInfo={moduleInfo} isViewOnly={isViewOnlyFeature} workshopLogo={logoUrl} workshopName={tenant?.workshop_name} />
          </aside>
        </>
      )}

      {/* ─── Main content (offset for desktop sidebar) ─── */}
      <main className="lg:pl-64 flex-1 min-w-0 pb-36 md:pb-20">
        <SubscriptionBanner />
        <Outlet />
      </main>

      {/* Help, pinned. The panel slides over whatever is open rather than
          navigating away — losing a half-finished invoice to ask a question
          is worse than the question. */}
      <button
        type="button"
        onClick={() => setHelpOpen(true)}
        title="Help"
        aria-label="Open help"
        className="fixed bottom-20 md:bottom-4 right-4 z-50 inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 ring-2 ring-white shadow-lg shadow-red-900/30 pl-3.5 pr-4 py-2.5 transition-colors"
        style={{ color: '#fff' }}
        data-testid="help-fab"
      >
        <LifeBuoy size={16} color="#fff" />
        <span className="text-sm font-bold" style={{ color: '#fff' }}>Help</span>
        {unseenNews > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-teal-500 text-slate-950 text-[10px] font-bold grid place-items-center"
            title={`${unseenNews} new ${unseenNews === 1 ? 'update' : 'updates'}`}
            data-testid="help-fab-badge"
          >
            {unseenNews}
          </span>
        )}
      </button>
      <HelpPanel open={helpOpen} onClose={() => { setHelpOpen(false); setUnseenNews(0); }} />
    </div>
  );
};

export default AdminLayout;
